import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  useDebtorCandidatesListQuery,
  useIssueLmsBlockMutation,
  useLiftLmsBlockMutation,
  DebtorCandidatesListDocument,
} from 'core/graphql';

import {
  buildLmsBlockVariables,
  ILmsAmountDue,
} from 'core/utils/lmsSuspension';

import { personName } from 'core/utils/personName';
import { useToasts } from '../Toast/useToasts';
import { lmsBlockErrorText } from './lmsBlockErrors';
import type { ILmsBlockActionOptions } from './useLmsAccessBlock';

export interface IDebtorCandidate {
  studentId: string;
  fullName: string;
  email: string;
  /**
   * Просрочка по валютам, PLN первой (сервер). Одно число `overdueAmount`
   * складывало остатки всех валют.
   */
  overdueAmounts: ILmsAmountDue[];
  overdueInvoiceCount: number;
  maxOverdueDays: number;
  currentlyBlocked: boolean;
}

interface IUseGroupDebtorCandidates {
  candidates: IDebtorCandidate[];
  loading: boolean;
  mutating: boolean;
  block: (
    studentId: string,
    options?: ILmsBlockActionOptions,
  ) => Promise<boolean>;
  unblock: (
    studentId: string,
    options?: ILmsBlockActionOptions,
  ) => Promise<boolean>;
}

const fullNameOf = (
  user?: {
    firstName?: string | null;
    lastName?: string | null;
    middleName?: string | null;
  } | null,
): string => personName(user, '—');

/**
 * Debtor candidates for a group (spec 0003) + per-row block/unblock. Candidates
 * already carry `currentlyBlocked`, so the row needs no second query; the
 * mutations refetch this query so the toggle repaints.
 */
export const useGroupDebtorCandidates = (
  groupId?: string,
  /**
   * `skip` — роль `debtorCandidates` не читает (`PAYMENT_ACCESS_ROLES`, на
   * карточке группы — HR): запрос не уходит, кандидатов нет.
   */
  queryOptions: { skip?: boolean } = {},
): IUseGroupDebtorCandidates => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const { data, loading } = useDebtorCandidatesListQuery({
    variables: { groupId },
    skip: !groupId || Boolean(queryOptions.skip),
    fetchPolicy: 'cache-and-network',
  });

  // Перезапрос списка после блокировки — только если роль его читает: иначе
  // `refetchQueries` отправил бы тот самый запрос, который мы выключили.
  const readsCandidates = Boolean(groupId) && !queryOptions.skip;
  const opts = useMemo(
    () => ({
      refetchQueries: readsCandidates
        ? [{ query: DebtorCandidatesListDocument, variables: { groupId } }]
        : [],
      awaitRefetchQueries: true,
    }),
    [groupId, readsCandidates],
  );

  const [blockMut, blockState] = useIssueLmsBlockMutation(opts);
  const [unblockMut, unblockState] = useLiftLmsBlockMutation(opts);

  const notifyError = useCallback(
    (text?: string) => {
      addToast({
        data: {
          type: ToastTypes.error,
          title: t('pages.studentDetail.lmsBlock.failed'),
          text,
        },
        type: GlobalToastTypeNames.LmsAccessBlock,
      });
    },
    [addToast, t],
  );

  const notifySuccess = useCallback(
    (title: string) => {
      addToast({
        data: {
          type: ToastTypes.success,
          title,
        },
        type: GlobalToastTypeNames.LmsAccessBlock,
      });
    },
    [addToast],
  );

  const block = useCallback(
    async (studentId: string, options?: ILmsBlockActionOptions) => {
      try {
        const res = await blockMut({
          variables: buildLmsBlockVariables(studentId, options),
        });
        const payload = res.data?.blockStudentLmsAccess;
        if (payload?.success) {
          notifySuccess(t('pages.studentDetail.lmsBlock.blocked'));
          return true;
        }
        notifyError(lmsBlockErrorText(payload?.customErrors, t));
        return false;
      } catch (e) {
        notifyError((e as Error).message);
        return false;
      }
    },
    [blockMut, notifyError, notifySuccess, t],
  );

  const unblock = useCallback(
    async (studentId: string, options?: ILmsBlockActionOptions) => {
      try {
        const res = await unblockMut({
          variables: buildLmsBlockVariables(studentId, options),
        });
        const payload = res.data?.unblockStudentLmsAccess;
        if (payload?.success) {
          notifySuccess(t('pages.studentDetail.lmsBlock.unblocked'));
          return true;
        }
        notifyError(lmsBlockErrorText(payload?.customErrors, t));
        return false;
      } catch (e) {
        notifyError((e as Error).message);
        return false;
      }
    },
    [unblockMut, notifyError, notifySuccess, t],
  );

  const candidates = useMemo<IDebtorCandidate[]>(
    () =>
      (data?.debtorCandidates || []).map((row) => ({
        studentId: row.student.id,
        fullName: fullNameOf(row.student.user),
        email: row.student.user?.email || '',
        overdueAmounts: row.overdueAmounts,
        overdueInvoiceCount: row.overdueInvoiceCount,
        maxOverdueDays: row.maxOverdueDays,
        currentlyBlocked: row.currentlyBlocked,
      })),
    [data?.debtorCandidates],
  );

  return {
    candidates,
    loading,
    mutating: blockState.loading || unblockState.loading,
    block,
    unblock,
  };
};
